/**
 * Automation rules — the words a rule is built from and read back in.
 *
 * A rule is one sentence: "When <trigger>, and only if <conditions>, <action>
 * and <action>…" (roadmap 3.3; migrations 817, 826, 832). The builder on the
 * Automations page IS that sentence, with a picker in each blank; this module
 * holds the pieces of the sentence, the starter rules, and the plain-words
 * read-back of a rule, a run, and a try-it preview.
 *
 * Words follow the product, not the database. Since #979 the person assigned
 * to an item is its OWNER ("Assign Item Owner"); the stored values the engine
 * reads (item_steward_assigned, assign_steward, recipients 'steward') keep
 * their names, the same way role ids did. Nothing on screen says "steward".
 *
 * A rule does up to five things. The first stays in actionKind / actionConfig
 * (where every rule written before 832 keeps it); the rest are moreActions.
 *
 * Pure: no network, no store, no component imports.
 */

import { getDegapPhaseLabel } from './semanticDefaults';

export type TriggerKind =
  | 'item_created'
  | 'item_exit_status_changed'
  | 'item_steward_assigned'
  | 'item_queue_changed'
  | 'item_tag_added'
  | 'item_idle_for_days'
  | 'item_became_ready'
  | 'cycle_phase_changed'
  | 'agent_finding_raised'
  | 'agent_finding_accepted';

export type ActionKind =
  | 'notify'
  | 'assign_steward'
  | 'move_queue'
  | 'add_tag'
  | 'set_exit_status'
  | 'send_webhook'
  | 'run_agent';

/** What a trigger is about. Decides which conditions and actions make sense. */
export type TriggerSubject = 'item' | 'cycle' | 'finding';

export interface TriggerMeta {
  kind: TriggerKind;
  subject: TriggerSubject;
  /** Runs on the background sweep rather than at the moment of the change. */
  needsSweep: boolean;
  /** The words in the builder's first picker. */
  label: string;
}

export const TRIGGERS: TriggerMeta[] = [
  { kind: 'item_exit_status_changed', subject: 'item', needsSweep: false, label: 'an item changes to' },
  { kind: 'item_created', subject: 'item', needsSweep: false, label: 'an item is created' },
  { kind: 'item_became_ready', subject: 'item', needsSweep: false, label: 'an item becomes ready for ranking' },
  { kind: 'item_steward_assigned', subject: 'item', needsSweep: false, label: 'an item is assigned to' },
  { kind: 'item_queue_changed', subject: 'item', needsSweep: false, label: 'an item moves to' },
  { kind: 'item_tag_added', subject: 'item', needsSweep: false, label: 'an item is tagged' },
  { kind: 'item_idle_for_days', subject: 'item', needsSweep: true, label: 'an item has not changed for' },
  { kind: 'cycle_phase_changed', subject: 'cycle', needsSweep: false, label: 'a cycle moves to' },
  { kind: 'agent_finding_raised', subject: 'finding', needsSweep: false, label: 'a finding is raised' },
  { kind: 'agent_finding_accepted', subject: 'finding', needsSweep: false, label: 'someone accepts a finding' },
];

export interface ActionMeta {
  kind: ActionKind;
  /** The words in the builder's action picker. */
  label: string;
  /** Changes the item, so it needs an item to act on. */
  changesItem: boolean;
  needsSweep: boolean;
}

export const ACTIONS: ActionMeta[] = [
  { kind: 'notify', label: 'notify', changesItem: false, needsSweep: false },
  { kind: 'assign_steward', label: 'assign it to', changesItem: true, needsSweep: false },
  { kind: 'move_queue', label: 'move it to', changesItem: true, needsSweep: false },
  { kind: 'add_tag', label: 'tag it', changesItem: true, needsSweep: false },
  { kind: 'set_exit_status', label: 'change it to', changesItem: true, needsSweep: false },
  { kind: 'run_agent', label: 'run', changesItem: false, needsSweep: false },
  { kind: 'send_webhook', label: 'send it to your webhooks', changesItem: false, needsSweep: true },
];

/** The engine's limit (migration 832): the first action and four more. */
export const MAX_ACTIONS = 5;

export const EXIT_STATES = ['active', 'deferred', 'archived'] as const;
export type ExitState = (typeof EXIT_STATES)[number];

export const DEGAP_PHASES = ['GATHER', 'ENGAGE', 'ARRANGE', 'PRIORITIZE', 'DECIDE'] as const;

export const SEVERITY_FLOORS = [
  { value: 'medium', label: 'medium or higher' },
  { value: 'high', label: 'high or higher' },
  { value: 'critical', label: 'critical only' },
] as const;

/** Ambient jobs a rule can ask to run now — the engine's run_agent list. */
export const RUN_AGENT_JOBS = [
  { value: 'beacon_scan', label: 'Beacon' },
  { value: 'review_due', label: 'Review Due' },
  { value: 'intelligence_scan', label: 'the intelligence agents' },
  { value: 'evidence_brief', label: 'the evidence brief' },
  { value: 'decision_narrative', label: 'the decision narrative' },
  { value: 'outcome_ledger', label: 'the outcome ledger' },
  { value: 'weekly_digest', label: 'the weekly digest' },
] as const;
export type RunAgentJob = (typeof RUN_AGENT_JOBS)[number]['value'];

/** The event a send-webhook rule goes out as. */
export const AUTOMATION_WEBHOOK_EVENT = 'automation.fired';

/** Conditions the builder offers, in the order it offers them. */
export const CONDITION_KEYS = ['queue_id', 'tags_any', 'item_category', 'steward_unassigned'] as const;
export type ConditionKey = (typeof CONDITION_KEYS)[number];

export const CONDITION_LABEL: Record<ConditionKey, string> = {
  queue_id: 'it is in',
  tags_any: 'it is tagged',
  item_category: 'its category is',
  steward_unassigned: 'it has no owner',
};

export interface RuleAction {
  kind: ActionKind;
  config: Record<string, unknown>;
}

export interface AutomationDraft {
  triggerKind: TriggerKind;
  triggerConfig: Record<string, unknown>;
  conditions: Record<string, unknown>;
  actionKind: ActionKind;
  actionConfig: Record<string, unknown>;
  /** Actions after the first, in order. */
  moreActions?: RuleAction[];
}

/** Names the page can resolve ids with. Everything optional. */
export interface RuleLookups {
  userName?: (id: string) => string | undefined;
  queueName?: (id: string) => string | undefined;
  groupName?: (id: string) => string | undefined;
  categoryLabel?: (slug: string) => string | undefined;
  agentName?: (agentType: string) => string | undefined;
}

export function triggerMeta(kind: TriggerKind): TriggerMeta {
  return TRIGGERS.find((t) => t.kind === kind) ?? TRIGGERS[0];
}

export function actionMeta(kind: ActionKind): ActionMeta {
  return ACTIONS.find((a) => a.kind === kind) ?? ACTIONS[0];
}

/** Every action of a rule, first one included, in order. */
export function allActions(d: AutomationDraft): RuleAction[] {
  return [{ kind: d.actionKind, config: d.actionConfig ?? {} }, ...(d.moreActions ?? [])];
}

/** The draft with its actions replaced (the list must not be empty). */
export function withActions(d: AutomationDraft, actions: RuleAction[]): AutomationDraft {
  const [first, ...rest] = actions;
  return { ...d, actionKind: first.kind, actionConfig: first.config, moreActions: rest };
}

/** A cycle has no item, so nothing that changes an item can follow it. */
export function actionsFor(trigger: TriggerKind): ActionMeta[] {
  const subject = triggerMeta(trigger).subject;
  return ACTIONS.filter((a) => subject !== 'cycle' || !a.changesItem);
}

/** Conditions test an item. Only item triggers have one for certain. */
export function takesConditions(trigger: TriggerKind): boolean {
  return triggerMeta(trigger).subject === 'item';
}

/** Whether this rule depends on the background sweep to do anything at all. */
export function needsSweep(d: AutomationDraft): boolean {
  return triggerMeta(d.triggerKind).needsSweep || allActions(d).some((a) => actionMeta(a.kind).needsSweep);
}

/** Who "the owner" is, given what the rule is about. Used inside the sentence. */
export function ownerPhrase(trigger: TriggerKind): string {
  switch (triggerMeta(trigger).subject) {
    case 'cycle':
      return 'the cycle’s owner';
    case 'finding':
      return 'the owner of the item it names';
    default:
      return 'the item’s owner';
  }
}

/**
 * The owner choice as the picker shows it. The engine notifies the workspace
 * admins when there is no owner to notify (migration 817,
 * automation_recipients), and the choice says so; the sentence stays short.
 */
export function ownerChoice(trigger: TriggerKind): string {
  return triggerMeta(trigger).subject === 'finding'
    ? 'the owner of the item it names, or the admins'
    : `${ownerPhrase(trigger)}, or the admins if it has none`;
}

export function notifyRecipientOptions(trigger: TriggerKind): Array<{ value: string; label: string }> {
  return [
    { value: 'steward', label: ownerChoice(trigger) },
    { value: 'admins', label: 'the workspace admins' },
    { value: 'users', label: 'people I pick' },
    { value: 'group', label: 'a group' },
  ];
}

function str(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

function stateWord(s: string): string {
  return s === 'active' ? 'active' : s === 'deferred' ? 'deferred' : s === 'archived' ? 'archived' : s;
}

function nameOr(fn: ((id: string) => string | undefined) | undefined, id: string, fallback: string): string {
  return (id && fn && fn(id)) || fallback;
}

/** "A, B and C" */
export function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

function tagsOf(v: unknown): string[] {
  return Array.isArray(v) ? (v as unknown[]).map((x) => String(x).trim()).filter(Boolean) : [];
}

const ORDINAL = ['first', 'second', 'third', 'fourth', 'fifth'];

// ── The sentence ─────────────────────────────────────────────────────────────

export function whenPhrase(d: AutomationDraft, l: RuleLookups = {}): string {
  const tc = d.triggerConfig ?? {};
  switch (d.triggerKind) {
    case 'item_created':
      return 'an item is created';
    case 'item_became_ready':
      return 'an item becomes ready for ranking';
    case 'item_exit_status_changed':
      return str(tc.to) ? `an item changes to ${stateWord(str(tc.to))}` : 'an item changes state';
    case 'item_steward_assigned':
      return str(tc.steward_id) ? `an item is assigned to ${nameOr(l.userName, str(tc.steward_id), 'someone')}` : 'an item is assigned to someone';
    case 'item_queue_changed':
      return str(tc.to_queue_id) ? `an item moves to ${nameOr(l.queueName, str(tc.to_queue_id), 'a queue')}` : 'an item moves to a queue';
    case 'item_tag_added':
      return str(tc.tag) ? `an item is tagged “${str(tc.tag)}”` : 'an item is tagged';
    case 'item_idle_for_days': {
      const n = Number(tc.days) || 0;
      return `an item has not changed for ${n} day${n === 1 ? '' : 's'}`;
    }
    case 'cycle_phase_changed':
      return str(tc.to_phase)
        ? `a cycle moves to ${getDegapPhaseLabel(str(tc.to_phase) as never)}`
        : 'a cycle moves to a new phase';
    case 'agent_finding_raised': {
      const who = str(tc.agent_type) ? nameOr(l.agentName, str(tc.agent_type), str(tc.agent_type)) : 'an agent';
      const floor = SEVERITY_FLOORS.find((s) => s.value === str(tc.min_severity));
      return `${who} raises a finding${floor ? ` rated ${floor.label}` : ''}`;
    }
    case 'agent_finding_accepted':
      return str(tc.agent_type)
        ? `someone accepts a finding from ${nameOr(l.agentName, str(tc.agent_type), str(tc.agent_type))}`
        : 'someone accepts a finding';
    default:
      return triggerMeta(d.triggerKind).label;
  }
}

export function conditionPhrase(key: ConditionKey, value: unknown, l: RuleLookups = {}): string {
  switch (key) {
    case 'queue_id':
      return `it is in ${nameOr(l.queueName, str(value), 'that queue')}`;
    case 'tags_any': {
      const tags = tagsOf(value);
      return `it is tagged ${tags.map((t) => `“${t}”`).join(' or ') || 'with that tag'}`;
    }
    case 'item_category':
      return `its category is ${nameOr(l.categoryLabel, str(value), str(value) || 'that category')}`;
    case 'steward_unassigned':
      return 'it has no owner';
  }
}

export function activeConditions(conditions: Record<string, unknown>): ConditionKey[] {
  return CONDITION_KEYS.filter((k) => {
    const v = conditions?.[k];
    if (k === 'steward_unassigned') return v === true;
    if (k === 'tags_any') return tagsOf(v).length > 0;
    return typeof v === 'string' ? v.trim() !== '' : v !== undefined && v !== null;
  });
}

/** One action, as words: "notify the item's owner", "tag it “stale”". */
export function actionPhrase(trigger: TriggerKind, a: RuleAction, l: RuleLookups = {}): string {
  const ac = a.config ?? {};
  switch (a.kind) {
    case 'notify': {
      const r = str(ac.recipients);
      if (r === 'users') {
        const ids = tagsOf(ac.user_ids);
        return `notify ${joinNames(ids.map((id) => nameOr(l.userName, id, 'someone'))) || 'the people you pick'}`;
      }
      if (r === 'group') return `notify everyone in ${nameOr(l.groupName, str(ac.group_id), 'the group')}`;
      if (r === 'admins') return 'notify the workspace admins';
      return `notify ${ownerPhrase(trigger)}`;
    }
    case 'assign_steward':
      return str(ac.steward) === 'queue_default'
        ? 'assign it to its queue’s default owner'
        : `assign it to ${nameOr(l.userName, str(ac.user_id), 'the person you pick')}`;
    case 'move_queue':
      return `move it to ${nameOr(l.queueName, str(ac.queue_id), 'the queue you pick')}`;
    case 'add_tag':
      return str(ac.tag) ? `tag it “${str(ac.tag)}”` : 'tag it';
    case 'set_exit_status':
      return triggerMeta(trigger).subject === 'finding'
        ? `ask a person to change it to ${stateWord(str(ac.to) || 'deferred')}`
        : `change it to ${stateWord(str(ac.to) || 'deferred')}`;
    case 'send_webhook':
      return 'send it to your webhooks';
    case 'run_agent':
      return `run ${RUN_AGENT_JOBS.find((j) => j.value === ac.job_type)?.label ?? 'an agent'} now`;
    default:
      return actionMeta(a.kind).label;
  }
}

/** Every action, joined: "notify the item's owner and tag it “stale”". */
export function thenPhrase(d: AutomationDraft, l: RuleLookups = {}): string {
  return joinNames(allActions(d).map((a) => actionPhrase(d.triggerKind, a, l)));
}

/** "When an item changes to deferred and it has no owner, notify the workspace admins." */
export function describeRule(d: AutomationDraft, l: RuleLookups = {}): string {
  const keys = takesConditions(d.triggerKind) ? activeConditions(d.conditions) : [];
  const conds = keys.map((k) => conditionPhrase(k, d.conditions[k], l));
  const only = conds.length ? ` and ${conds.join(' and ')}` : '';
  return `When ${whenPhrase(d, l)}${only}, ${thenPhrase(d, l)}.`;
}

/** The rule's stored name: its sentence, without the closing full stop. */
export function ruleName(d: AutomationDraft, l: RuleLookups = {}): string {
  return describeRule(d, l).replace(/\.$/, '');
}

// ── The notification a notify action sends ──────────────────────────────────

/**
 * The notification's title, written for the person who receives it. The
 * engine puts the item's, cycle's or finding's name where {{subject}} is.
 * Nobody types this placeholder; the builder shows the title with the name.
 */
export function notifyTitle(d: AutomationDraft, l: RuleLookups = {}): string {
  const tc = d.triggerConfig ?? {};
  switch (d.triggerKind) {
    case 'item_created':
      return 'New item: {{subject}}';
    case 'item_became_ready':
      return '{{subject}} is ready for ranking';
    case 'item_exit_status_changed':
      return str(tc.to) ? `{{subject}} is now ${stateWord(str(tc.to))}` : '{{subject}} changed state';
    case 'item_steward_assigned':
      return '{{subject}} has a new owner';
    case 'item_queue_changed':
      return str(tc.to_queue_id) ? `{{subject}} moved to ${nameOr(l.queueName, str(tc.to_queue_id), 'a new queue')}` : '{{subject}} moved to a new queue';
    case 'item_tag_added':
      return str(tc.tag) ? `{{subject}} was tagged “${str(tc.tag)}”` : '{{subject}} has a new tag';
    case 'item_idle_for_days': {
      const n = Number(tc.days) || 0;
      return `{{subject}} has not changed in ${n} day${n === 1 ? '' : 's'}`;
    }
    case 'cycle_phase_changed':
      return str(tc.to_phase)
        ? `{{subject}} moved to ${getDegapPhaseLabel(str(tc.to_phase) as never)}`
        : '{{subject}} moved to a new phase';
    case 'agent_finding_raised':
      return 'New finding: {{subject}}';
    case 'agent_finding_accepted':
      return 'Finding accepted: {{subject}}';
    default:
      return '{{subject}}';
  }
}

/** What the subject is called, for showing the title before it has a name. */
export function subjectWord(trigger: TriggerKind): string {
  const s = triggerMeta(trigger).subject;
  return s === 'cycle' ? 'cycle name' : s === 'finding' ? 'finding' : 'item name';
}

// ── Checking a draft ─────────────────────────────────────────────────────────

function missingInAction(a: RuleAction): string | null {
  const ac = a.config ?? {};
  switch (a.kind) {
    case 'notify': {
      const r = str(ac.recipients);
      if (r === 'users' && tagsOf(ac.user_ids).length === 0) return 'pick at least one person to notify.';
      if (r === 'group' && !str(ac.group_id)) return 'pick the group to notify.';
      return null;
    }
    case 'assign_steward':
      return str(ac.steward) !== 'queue_default' && !str(ac.user_id) ? 'pick the person to assign it to.' : null;
    case 'move_queue':
      return !str(ac.queue_id) ? 'pick the queue to move it to.' : null;
    case 'add_tag':
      return !str(ac.tag) ? 'write the tag.' : null;
    case 'run_agent':
      return !RUN_AGENT_JOBS.some((j) => j.value === ac.job_type) ? 'pick what to run.' : null;
    default:
      return null;
  }
}

/** First letter up, the rest as given. Shared: describeAgent reads it too. */
export function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * The same shape rules the database enforces (automation_check_shape and
 * automation_check_more_actions), in plain words, so a person sees what is
 * missing while building rather than after the save. Null when complete.
 */
export function missingPiece(d: AutomationDraft): string | null {
  const tc = d.triggerConfig ?? {};
  if (d.triggerKind === 'item_idle_for_days') {
    const n = Number(tc.days);
    if (!Number.isInteger(n) || n < 1) return 'Set the number of days, 1 or more.';
  }
  const actions = allActions(d);
  if (actions.length > MAX_ACTIONS) return `A rule can do at most ${MAX_ACTIONS} things.`;
  const allowed = actionsFor(d.triggerKind);
  for (let i = 0; i < actions.length; i++) {
    if (!allowed.some((a) => a.kind === actions[i].kind)) {
      return 'A cycle has no item to change. Choose notify, run or send it to your webhooks.';
    }
    const gap = missingInAction(actions[i]);
    if (gap) return actions.length > 1 ? `In the ${ORDINAL[i]} action, ${gap}` : capitalize(gap);
  }
  if (takesConditions(d.triggerKind)) {
    const c = d.conditions ?? {};
    if ('queue_id' in c && !str(c.queue_id)) return 'Pick the queue in the condition, or remove it.';
    if ('tags_any' in c && tagsOf(c.tags_any).length === 0) return 'Write the tag in the condition, or remove it.';
    if ('item_category' in c && !str(c.item_category)) return 'Pick the category in the condition, or remove it.';
  }
  return null;
}

function savedAction(d: AutomationDraft, a: RuleAction, l: RuleLookups): RuleAction {
  const config = { ...(a.config ?? {}) };
  if (a.kind === 'notify') {
    config.title = notifyTitle(d, l);
    if (!str(config.body)) delete config.body;
    if (str(config.recipients) !== 'users') delete config.user_ids;
    if (str(config.recipients) !== 'group') delete config.group_id;
  }
  if (a.kind === 'send_webhook') config.event = AUTOMATION_WEBHOOK_EVENT;
  if (a.kind === 'assign_steward' && str(config.steward) === 'queue_default') delete config.user_id;
  return { kind: a.kind, config };
}

/**
 * What the database is given: the draft with the conditions an item trigger
 * cannot use removed, and each notification title written from the trigger.
 */
export function toSaved(d: AutomationDraft, l: RuleLookups = {}): AutomationDraft & { name: string; moreActions: RuleAction[] } {
  const conditions: Record<string, unknown> = {};
  if (takesConditions(d.triggerKind)) {
    for (const k of activeConditions(d.conditions)) conditions[k] = d.conditions[k];
  }
  const actions = allActions(d).map((a) => savedAction(d, a, l));
  const saved = withActions({ ...d, conditions }, actions);
  return { ...saved, moreActions: saved.moreActions ?? [], name: ruleName(saved, l) };
}

/** A fresh config for an action, with each blank set to the most common answer. */
export function blankActionConfig(kind: ActionKind): Record<string, unknown> {
  switch (kind) {
    case 'notify':
      return { recipients: 'steward' };
    case 'assign_steward':
      return { steward: 'user' };
    case 'set_exit_status':
      return { to: 'deferred' };
    case 'run_agent':
      return { job_type: 'beacon_scan' };
    case 'send_webhook':
      return { event: AUTOMATION_WEBHOOK_EVENT };
    default:
      return {};
  }
}

export function blankTriggerConfig(kind: TriggerKind): Record<string, unknown> {
  switch (kind) {
    case 'item_exit_status_changed':
      return { to: 'deferred' };
    case 'item_idle_for_days':
      return { days: 30 };
    default:
      return {};
  }
}

// ── Starting points ──────────────────────────────────────────────────────────

export const STARTER_RULES: AutomationDraft[] = [
  {
    triggerKind: 'item_exit_status_changed',
    triggerConfig: { to: 'deferred' },
    conditions: {},
    actionKind: 'notify',
    actionConfig: { recipients: 'steward' },
  },
  {
    triggerKind: 'item_became_ready',
    triggerConfig: {},
    conditions: {},
    actionKind: 'notify',
    actionConfig: { recipients: 'admins' },
  },
  {
    triggerKind: 'agent_finding_raised',
    triggerConfig: { min_severity: 'high' },
    conditions: {},
    actionKind: 'notify',
    actionConfig: { recipients: 'admins' },
  },
  {
    triggerKind: 'item_idle_for_days',
    triggerConfig: { days: 30 },
    conditions: {},
    actionKind: 'add_tag',
    actionConfig: { tag: 'stale' },
    moreActions: [{ kind: 'notify', config: { recipients: 'steward' } }],
  },
];

// ── Reading back what happened ───────────────────────────────────────────────

export function runStatusLabel(status: string): string {
  switch (status) {
    case 'completed':
      return 'Done';
    case 'queued':
      return 'Waiting for the background run';
    case 'failed':
      return 'Failed';
    case 'skipped':
      return 'Nothing to do';
    case 'proposed':
      return 'Asked a person';
    default:
      return status;
  }
}

/** The reason codes the engine writes into a run's detail, in words. */
export function runReasonLabel(reason: string | undefined): string | null {
  if (!reason) return null;
  const map: Record<string, string> = {
    no_recipient_resolved: 'there was no one to notify',
    no_item_to_act_on: 'there was no item to act on',
    item_already_has_a_steward: 'the item already has an owner',
    no_steward_resolved: 'there was no one to assign it to',
    already_in_queue: 'the item is already in that queue',
    tag_already_present: 'the item already has that tag',
    already_in_that_state: 'the item is already in that state',
    agent_triggered_rules_propose_state_changes: 'an agent started this rule, so a person was asked instead',
    rule_author_no_longer_admin: 'the person who wrote the rule is no longer an admin here',
    ambient_job_off_or_absent: 'that job is switched off',
    no_webhook_subscribed: 'no webhook address listens for it',
    unknown_action: 'the action is not one the engine knows',
  };
  return map[reason] ?? reason.replace(/_/g, ' ');
}

export interface PreviewAction {
  kind: ActionKind;
  status: string;
  detail: Record<string, unknown>;
  recipients: string[];
  /** A notify-the-owner action reached the admins because the item has no owner. */
  ownerMissing: boolean;
}

export interface PreviewResult {
  matches: boolean;
  unmet?: string[];
  status?: string;
  detail?: Record<string, unknown>;
  recipients?: string[];
  itemTitle?: string;
  /** Each action's answer, in order (migration 832). */
  actions?: PreviewAction[];
}

/** What doing an action is called, for "skip tagging it". */
const ACTION_GERUND: Record<ActionKind, string> = {
  notify: 'notifying anyone',
  assign_steward: 'assigning it',
  move_queue: 'moving it',
  add_tag: 'tagging it',
  set_exit_status: 'changing its state',
  send_webhook: 'sending it to your webhooks',
  run_agent: 'running the agent',
};

function previewFragment(trigger: TriggerKind, a: RuleAction, p: PreviewAction, l: RuleLookups): string {
  const detail = p.detail ?? {};
  const reason = typeof detail.reason === 'string' ? detail.reason : undefined;
  switch (p.status) {
    case 'skipped':
      return `skip ${ACTION_GERUND[a.kind] ?? 'this'}, because ${runReasonLabel(reason) ?? 'there was nothing to do'}`;
    case 'failed':
      return `fail at ${ACTION_GERUND[a.kind] ?? 'this'}: ${typeof detail.error === 'string' ? detail.error : runReasonLabel(reason) ?? 'unknown error'}`;
    case 'queued':
      return 'send it to your webhooks on the next background run';
    case 'proposed':
      return `ask the workspace admins and the item’s owner to change it to ${stateWord(str(detail.to))}, without changing it`;
    case 'completed':
      break;
    default:
      return actionPhrase(trigger, a, l);
  }
  switch (a.kind) {
    case 'notify': {
      const names = joinNames(p.recipients ?? []) || 'no one';
      return p.ownerMissing
        ? `notify the workspace admins, ${names}, because the item has no owner`
        : `notify ${names}`;
    }
    case 'assign_steward':
      return `assign it to ${nameOr(l.userName, str(detail.steward_id), 'that person')}`;
    case 'move_queue':
      return `move it to ${nameOr(l.queueName, str(detail.queue_id), 'that queue')}`;
    case 'add_tag':
      return `tag it “${str(detail.tag)}”`;
    case 'set_exit_status':
      return `change it from ${stateWord(str(detail.from))} to ${stateWord(str(detail.to))}`;
    case 'run_agent':
      return `run ${RUN_AGENT_JOBS.find((j) => j.value === detail.job_type)?.label ?? 'that job'} on the next background run`;
    default:
      return actionPhrase(trigger, a, l);
  }
}

/** The try-it answer, as one plain sentence. */
export function previewSentence(p: PreviewResult, d: AutomationDraft, l: RuleLookups = {}): string {
  if (!p.matches) {
    const why = (p.unmet ?? [])
      .filter((k): k is ConditionKey => (CONDITION_KEYS as readonly string[]).includes(k))
      .map((k) => {
        const v = d.conditions[k];
        switch (k) {
          case 'queue_id':
            return `it is not in ${nameOr(l.queueName, str(v), 'that queue')}`;
          case 'tags_any':
            return `it is not tagged ${tagsOf(v).map((t) => `“${t}”`).join(' or ')}`;
          case 'item_category':
            return `its category is not ${nameOr(l.categoryLabel, str(v), str(v))}`;
          case 'steward_unassigned':
            return 'it already has an owner';
        }
      });
    return `The rule would skip this item: ${joinNames(why) || 'it does not meet the conditions'}.`;
  }
  const actions = allActions(d);
  const answers: PreviewAction[] =
    p.actions && p.actions.length
      ? p.actions
      : [{ kind: d.actionKind, status: p.status ?? 'completed', detail: p.detail ?? {}, recipients: p.recipients ?? [], ownerMissing: false }];
  const parts = answers.map((ans, i) => previewFragment(d.triggerKind, actions[i] ?? { kind: ans.kind, config: {} }, ans, l));
  return `The rule would ${parts.join(', then ')}.`;
}

// ── Whether a rule can run here ──────────────────────────────────────────────

/** What the workspace has set up that a rule may depend on. */
export interface RuleReadiness {
  /** The ambient runtime module is on. */
  backgroundJobsOn: boolean;
  /** ambient job type → switched on. */
  jobsOn: Record<string, boolean>;
  /** Registered webhook addresses, and how many of them take automation.fired. */
  webhookAddresses: number;
  webhookListeners: number;
  /** At least one queue names a default owner. */
  anyQueueHasDefaultOwner: boolean;
}

export type RuleBlockerKey =
  | 'background_jobs_off'
  | 'sweep_off'
  | 'job_off'
  | 'no_webhook_listener'
  | 'no_queue_default_owner';

export interface RuleBlocker {
  key: RuleBlockerKey;
  /** For job_off: the ambient job that is switched off. */
  job?: string;
}

/**
 * The reasons this rule would do nothing in this workspace, each of which has
 * one fix. Empty when nothing stands in its way. The builder shows each with
 * the control that fixes it; the rule list marks a rule that has any.
 */
export function ruleBlockers(d: AutomationDraft, r: RuleReadiness): RuleBlocker[] {
  const out: RuleBlocker[] = [];
  const actions = allActions(d);
  const jobs = Array.from(new Set(actions.filter((a) => a.kind === 'run_agent').map((a) => String(a.config?.job_type ?? '')).filter(Boolean)));
  if (!r.backgroundJobsOn && (needsSweep(d) || jobs.length > 0)) {
    out.push({ key: 'background_jobs_off' });
  } else {
    if (needsSweep(d) && !r.jobsOn.automation_sweep) out.push({ key: 'sweep_off' });
    for (const job of jobs) if (!r.jobsOn[job]) out.push({ key: 'job_off', job });
  }
  if (actions.some((a) => a.kind === 'send_webhook') && r.webhookListeners === 0) out.push({ key: 'no_webhook_listener' });
  if (actions.some((a) => a.kind === 'assign_steward' && a.config?.steward === 'queue_default') && !r.anyQueueHasDefaultOwner) {
    out.push({ key: 'no_queue_default_owner' });
  }
  return out;
}

/** A short name for what a run did, for the run log. */
export const ACTION_SHORT: Record<ActionKind, string> = {
  notify: 'notify',
  assign_steward: 'assign owner',
  move_queue: 'move queue',
  add_tag: 'add tag',
  set_exit_status: 'change state',
  send_webhook: 'send webhook',
  run_agent: 'run agent',
};

/** One sentence on why a run did nothing or asked a person instead, or null. */
export function runOutcomeNote(status: string, detail: Record<string, unknown>): string | null {
  const reason = typeof detail.reason === 'string' ? detail.reason : undefined;
  const err = typeof detail.error === 'string' ? detail.error : undefined;
  if (status === 'proposed') return 'An agent started it, so a person was asked to make the change.';
  if (status === 'skipped') return `It did nothing: ${runReasonLabel(reason) ?? 'there was nothing to do'}.`;
  if (status === 'failed') return `It failed: ${err ?? runReasonLabel(reason) ?? 'unknown error'}.`;
  return null;
}
